<template>
    <section class="x-posts-section">
        <div class="container">
            <div class="section-header">
                <h2 class="section-title">{{ title }}</h2>
            </div>
            
            <div class="waterfall-flow">
                <div 
                    v-for="(tweet, index) in tweets" 
                    :key="index" 
                    :data-tweet-id="getTweetId(tweet)" 
                    v-html="tweet"
                    class="twitter-embed-container"
                ></div>
            </div>
        </div>
    </section>
</template>

<script setup lang="ts">
import { onMounted, nextTick } from 'vue'

// Props
interface Props {
    title: string
    tweetUrls: string[]
}

const props = defineProps<Props>()

// Generate Twitter embed HTML from URLs
const tweets = props.tweetUrls.map(url => {
    const match = url.match(/status\/(\d+)/)
    const statusId = match ? match[1] : ''
    const userMatch = url.match(/(?:twitter\.com|x\.com)\/([^\/]+)/)
    const username = userMatch ? userMatch[1] : ''

    // Convert x.com to twitter.com for better compatibility
    const twitterUrl = url.replace('x.com', 'twitter.com')

    return `<blockquote class="twitter-tweet" data-theme="dark"><p dir="ltr" lang="en">
        <div class="tweet-skeleton">
            <div class="skeleton-header">
                <div class="skeleton-avatar"></div>
                <div class="skeleton-text-group">
                    <div class="skeleton-text skeleton-text-short"></div>
                    <div class="skeleton-text skeleton-text-shorter"></div>
                </div>
            </div>
            <div class="skeleton-content">
                <div class="skeleton-text skeleton-text-long"></div>
                <div class="skeleton-text skeleton-text-medium"></div>
                <div class="skeleton-text skeleton-text-short"></div>
            </div>
            <div class="skeleton-footer">
                <div class="skeleton-text skeleton-text-shorter"></div>
            </div>
        </div>
    </p>— ${username} (@${username}) <a href="${twitterUrl}?ref_src=twsrc%5Etfw" rel="nofollow noopener" target="_blank">December 20, 2024</a></blockquote>`
})

// Extract tweet ID from HTML
const getTweetId = (tweetHtml: string) => {
    const match = tweetHtml.match(/status\/(\d+)/)
    return match ? match[1] : ''
}

// Global error handler for Twitter embed errors
const setupErrorHandler = () => {
    // Capture unhandled promise rejections from Twitter embed
    window.addEventListener('unhandledrejection', (event) => {
        if (event.reason && typeof event.reason === 'object') {
            const errorMessage = event.reason.message || String(event.reason)
            if (errorMessage.includes('appendChild') || errorMessage.includes('embed')) {
                // Suppress Twitter embed errors - they don't affect functionality
                event.preventDefault()
                return false
            }
        }
    })

    // Capture general errors from Twitter embed
    const originalError = window.onerror
    window.onerror = (message, source, lineno, colno, error) => {
        if (typeof message === 'string' && 
            (message.includes('appendChild') || message.includes('embed.') || message.includes('twitter'))) {
            // Suppress Twitter embed errors
            return true
        }
        // Call original error handler if it exists
        if (originalError) {
            return originalError(message, source, lineno, colno, error)
        }
        return false
    }
}

// Twitter Widget Loading
onMounted(() => {
    // Setup error handler first
    setupErrorHandler()

    // Wait for DOM to be fully ready
    nextTick(() => {
        // Delayed loading of Twitter script to avoid conflicts with other scripts
        setTimeout(() => {
            try {
                // Check if Twitter script is already loaded
                if (document.querySelector('script[src*="platform.twitter.com/widgets.js"]')) {
                    // If script exists, just reload widgets after ensuring DOM is ready
                    nextTick(() => {
                        setTimeout(() => {
                            const twttr = (window as { twttr?: { widgets?: { load: () => void } } }).twttr
                            if (twttr && twttr.widgets && typeof twttr.widgets.load === 'function') {
                                try {
                                    twttr.widgets.load()
                                } catch (error) {
                                    // Silently handle Twitter widget errors
                                }
                            }
                        }, 200)
                    })
                    return
                }

                const script = document.createElement('script')
                script.src = 'https://platform.twitter.com/widgets.js'
                script.async = true
                script.charset = 'utf-8'
                script.crossOrigin = 'anonymous'
                
                // Add error handling for script loading
                script.onerror = () => {
                    console.warn('Failed to load Twitter widgets script')
                }
                
                // Safely add to head
                if (document.head && document.head instanceof Node) {
                    document.head.appendChild(script)
                }
                
                // Initialize Twitter widgets after script loads with error handling
                script.onload = () => {
                    try {
                        // Wait for Twitter script to fully load and DOM to be ready
                        nextTick(() => {
                            setTimeout(() => {
                                const twttr = (window as { twttr?: { widgets?: { load: () => void } } }).twttr
                                if (twttr && twttr.widgets && typeof twttr.widgets.load === 'function') {
                                    try {
                                        twttr.widgets.load()
                                    } catch (error) {
                                        // Silently handle Twitter widget errors
                                    }
                                }
                            }, 300)
                        })
                    } catch (error) {
                        // Silently handle initialization errors
                    }
                }
            } catch (error) {
                console.warn('Error loading Twitter widgets:', error)
            }
        }, 1500) // Increased delay to ensure DOM is ready
    })
})
</script>

<style lang="less" scoped>
.container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 1rem;
}

/* X Posts Section */
.x-posts-section {
    padding: 6rem 0;
    background-color: #22242d;
}

.section-header {
    text-align: center;
    margin-bottom: 3rem;
}

.section-title {
    font-size: 3rem;
    font-weight: 700;
    margin-bottom: 1rem;
    color: white;
    line-height: 1.2;
}

.waterfall-flow {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 20px;
    max-width: 1400px;
    margin: 0 auto;

    @media (max-width: 1200px) {
        grid-template-columns: repeat(3, 1fr);
    }

    @media (max-width: 768px) {
        grid-template-columns: repeat(2, 1fr);
        gap: 0.75rem;
    }

    @media (max-width: 480px) {
        grid-template-columns: 1fr;
    }

    :deep(.twitter-tweet) {
        margin: 0 !important;
        max-width: 100% !important;
        width: 100% !important;
    }

    .twitter-embed-container {
        position: relative;

        /* 确保容器内的内容不会导致布局问题 */
        :deep(*) {
            max-width: 100% !important;
            box-sizing: border-box;
        }

        :deep(iframe) {
            max-width: 100% !important;
            width: 100% !important;
            border-radius: 16px !important;
        }

        /* Tweet Skeleton Loader */
        :deep(.tweet-skeleton) {
            padding: 1.5rem;
            background: rgba(255, 255, 255, 0.05);
            border-radius: 16px;
            border: 1px solid rgba(255, 255, 255, 0.08);
            animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
            min-height: 360px;
        }

        :deep(.skeleton-header) {
            display: flex;
            align-items: center;
            gap: 0.75rem;
            margin-bottom: 1rem;
        }

        :deep(.skeleton-avatar) {
            width: 48px;
            height: 48px;
            border-radius: 50%;
            background: linear-gradient(90deg, rgba(255, 255, 255, 0.08) 25%, rgba(255, 255, 255, 0.12) 50%, rgba(255, 255, 255, 0.08) 75%);
            background-size: 200% 100%;
            animation: shimmer 1.5s ease-in-out infinite;
        }

        :deep(.skeleton-text-group) {
            flex: 1;
            display: flex;
            flex-direction: column;
            gap: 0.5rem;
        }

        :deep(.skeleton-text) {
            height: 12px;
            border-radius: 6px;
            background: linear-gradient(90deg, rgba(255, 255, 255, 0.08) 25%, rgba(255, 255, 255, 0.12) 50%, rgba(255, 255, 255, 0.08) 75%);
            background-size: 200% 100%;
            animation: shimmer 1.5s ease-in-out infinite;
        }

        :deep(.skeleton-text-short) {
            width: 60%;
        }

        :deep(.skeleton-text-shorter) {
            width: 40%;
        }

        :deep(.skeleton-text-medium) {
            width: 80%;
        }

        :deep(.skeleton-text-long) {
            width: 100%;
        }

        :deep(.skeleton-content) {
            display: flex;
            flex-direction: column;
            gap: 0.5rem;
            margin-bottom: 1rem;
        }

        :deep(.skeleton-footer) {
            display: flex;
            gap: 1rem;
        }

        @keyframes shimmer {
            0% {
                background-position: 200% 0;
            }
            100% {
                background-position: -200% 0;
            }
        }

        @keyframes pulse {
            0%, 100% {
                opacity: 1;
            }
            50% {
                opacity: 0.8;
            }
        }
    }
}

/* Responsive Design */
@media (max-width: 768px) {
    .x-posts-section {
        padding: 4rem 0;
    }

    .container {
        padding: 0 0.875rem;
    }

    .section-header {
        margin-bottom: 2rem;
    }

    .section-title {
        font-size: 2rem;
    }

    .waterfall-flow {
        grid-template-columns: 1fr;
        gap: 1rem;
    }

}

@media (max-width: 480px) {
    .x-posts-section {
        padding: 3rem 0;
    }

    .section-title {
        font-size: 1.5rem;
    }

    .container {
        padding: 0 0.75rem;
    }

}
</style>
